import React from 'react';

/**
 * VendorCaptureCard — "We found contact information on your document."
 *
 * Rendered in the lower portion of the add-items review modal (above the
 * Save action bar) when the AI invoice reader found a supplier contact block
 * on an imported document that is NOT the store's own letterhead. Lets the
 * user choose, before saving, whether to create a new vendor from it, attach
 * the details to an existing vendor (blank fields only — user edits are never
 * overwritten), or skip vendor capture entirely.
 *
 * Purely presentational + controlled: the mount wrapper owns the choice state
 * and forwards the final choices to the approve handler. Vendor records are
 * written server-side by store-invoice-processor `record_upload`, the same
 * path emailed price-import invoices use.
 *
 * captures: [{
 *   key,               // stable id for this capture (per source document)
 *   supplierName,      // vendor name as read off the document
 *   sourceFile,        // file the details were read from (shown when > 1 file)
 *   contact,           // { legal_name, contact_name, order_email, phone, address, payment_terms, account_number }
 *   resolution,        // { status: 'matched'|'new', vendorId, vendorName, confidencePct } | null
 *   vendors,           // [{ id, displayName }] for the "existing vendor" picker
 *   choice,            // { mode: 'new'|'existing'|'skip', vendorId } (controlled)
 * }]
 * onChangeChoice(key, choice)
 */

const C = {
  text: '#5A4A42',
  muted: '#8B7B73',
  border: '#E8DBCE',
  bg: '#FFFDF9',
  innerBg: '#FCFAF8',
  innerBorder: '#EFE7E0',
  green: '#2F5D3A',
  greenBg: '#E3F0E4',
  greenBorder: '#B9DBBE',
  amber: '#7A5A0A',
  amberBg: '#FFF0BD',
  amberBorder: '#E6CD83',
};

const CONTACT_FIELDS = [
  ['legal_name', 'Legal name'],
  ['contact_name', 'Contact'],
  ['order_email', 'Order email'],
  ['phone', 'Phone'],
  ['address', 'Address'],
  ['payment_terms', 'Terms'],
  ['account_number', 'Account #'],
];

function ChoiceRow({ checked, disabled, onSelect, children }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
      padding: '0.5rem 0.65rem', borderRadius: 8, cursor: 'pointer',
      border: `1px solid ${checked ? '#C8B7A6' : 'transparent'}`,
      background: checked ? '#FFF' : 'transparent',
      opacity: disabled ? 0.6 : 1,
    }}>
      <input
        type="radio"
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
        style={{ marginTop: 2, accentColor: '#5A4A42', cursor: 'pointer' }}
      />
      <span style={{ flex: 1, minWidth: 0, fontSize: '0.84rem', color: C.text }}>{children}</span>
    </label>
  );
}

function SingleCapture({ capture, onChangeChoice, showSource }) {
  const { key, supplierName, sourceFile, contact, resolution, vendors, choice } = capture;
  const mode = choice?.mode || 'new';
  const contactRows = CONTACT_FIELDS
    .map(([field, label]) => [label, contact ? contact[field] : null])
    .filter(([, value]) => value != null && String(value).trim() !== '');
  const matched = resolution?.status === 'matched' && resolution.vendorId;
  const vendorList = Array.isArray(vendors) ? vendors : [];
  const selectedVendorId = choice?.vendorId
    ? String(choice.vendorId)
    : (matched ? String(resolution.vendorId) : (vendorList[0] ? String(vendorList[0].id) : ''));

  const set = (next) => onChangeChoice(key, { mode, vendorId: selectedVendorId, ...next });

  return (
    <div style={{ marginTop: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, color: C.text, fontSize: '0.9rem' }}>
          {supplierName || 'Unnamed supplier'}
        </span>
        {matched ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            color: C.green, background: C.greenBg, border: `1px solid ${C.greenBorder}`,
            borderRadius: 999, padding: '0.15rem 0.55rem', fontSize: '0.72rem', fontWeight: 700,
          }}>
            ✓ Looks like your vendor “{resolution.vendorName}”
          </span>
        ) : (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            color: C.amber, background: C.amberBg, border: `1px solid ${C.amberBorder}`,
            borderRadius: 999, padding: '0.15rem 0.55rem', fontSize: '0.72rem', fontWeight: 700,
          }}>
            Not in your vendor list yet
          </span>
        )}
        {showSource && sourceFile && (
          <span style={{ color: C.muted, fontSize: '0.74rem' }}>from {sourceFile}</span>
        )}
      </div>

      {contactRows.length > 0 && (
        <div style={{
          marginTop: '0.55rem', background: C.innerBg,
          border: `1px solid ${C.innerBorder}`, borderRadius: 8, padding: '0.6rem 0.75rem',
        }}>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.2rem 0.75rem', margin: 0 }}>
            {contactRows.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt style={{ color: C.muted, fontSize: '0.74rem', fontWeight: 700 }}>{label}</dt>
                <dd style={{ color: C.text, fontSize: '0.78rem', margin: 0, minWidth: 0, wordBreak: 'break-word' }}>{String(value)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      )}

      <div style={{ marginTop: '0.55rem', display: 'grid', gap: '0.25rem' }}>
        <ChoiceRow checked={mode === 'new'} onSelect={() => set({ mode: 'new' })}>
          Create a new vendor {supplierName ? <strong>“{supplierName}”</strong> : ''} with these details
        </ChoiceRow>
        <ChoiceRow
          checked={mode === 'existing'}
          disabled={vendorList.length === 0}
          onSelect={() => set({ mode: 'existing' })}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span>Add the details to an existing vendor</span>
            <select
              value={selectedVendorId}
              disabled={mode !== 'existing'}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => set({ mode: 'existing', vendorId: e.target.value })}
              aria-label="Existing vendor to update"
              style={{
                border: `1px solid ${C.border}`, borderRadius: 6, padding: '0.28rem 0.4rem',
                fontSize: '0.8rem', color: C.text, background: '#fff', maxWidth: 240,
              }}
            >
              {vendorList.map((v) => (
                <option key={v.id} value={String(v.id)}>{v.displayName || 'Unnamed vendor'}</option>
              ))}
            </select>
          </span>
        </ChoiceRow>
        <ChoiceRow checked={mode === 'skip'} onSelect={() => set({ mode: 'skip' })}>
          Don’t save vendor info from this document
        </ChoiceRow>
      </div>
    </div>
  );
}

export default function VendorCaptureCard({ captures = [], onChangeChoice }) {
  const list = captures.filter(Boolean);
  if (list.length === 0) return null;
  const showSource = list.length > 1;
  return (
    <div
      role="group"
      aria-label="Vendor contact information found on your document"
      style={{
        background: '#FFFDF9', border: '1px solid #E8DBCE', borderRadius: 10,
        padding: '0.8rem 1rem 0.9rem', margin: '0.9rem 0 0.75rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span aria-hidden="true">📇</span>
        <span style={{ fontWeight: 800, color: C.text, fontSize: '0.92rem' }}>
          We found contact information on your document
        </span>
      </div>
      <p style={{ color: C.muted, fontSize: '0.8rem', margin: '0.3rem 0 0', lineHeight: 1.45 }}>
        Save it with your items so ordering from this supplier is ready to go. Only blank fields on
        the vendor’s record are filled — anything you already entered is kept.
      </p>
      {list.map((capture) => (
        <SingleCapture
          key={capture.key}
          capture={capture}
          onChangeChoice={onChangeChoice}
          showSource={showSource}
        />
      ))}
    </div>
  );
}
